// Task #3041 — Registry stili avatar del PA (DiceBear v9, SVG locali).
//
// Generazione deterministica da (style, seed, options): nessun servizio
// esterno a runtime, l'SVG è prodotto in-process da @dicebear/core e reso
// come data-URI. Le opzioni condivise (incarnato/capelli/sfondo/flip) sono
// mappate qui sulle chiavi native di ogni stile (es. micah→baseColor,
// open-peeps→headContrastColor); gli stili che non supportano un'opzione la
// ignorano (il controllo non viene mostrato in galleria).

import { createAvatar, type Result } from '@dicebear/core';
import { adventurer, lorelei, micah, notionists, openPeeps, personas } from '@dicebear/collection';
import type { PaAvatarConfig, PaAvatarStyle } from '@easyflow/shared';

export interface PaAvatarStyleDef {
  id: PaAvatarStyle;
  label: string;
  /** Lo stile supporta il colore dell'incarnato. */
  supportsSkin: boolean;
  /** Lo stile supporta il colore dei capelli (o equivalente). */
  supportsHair: boolean;
}

export const PA_AVATAR_STYLE_DEFS: readonly PaAvatarStyleDef[] = [
  { id: 'personas',   label: 'Personas',   supportsSkin: true,  supportsHair: true },
  { id: 'notionists', label: 'Notionists', supportsSkin: false, supportsHair: false },
  { id: 'lorelei',    label: 'Lorelei',    supportsSkin: true,  supportsHair: true },
  { id: 'micah',      label: 'Micah',      supportsSkin: true,  supportsHair: true },
  { id: 'adventurer', label: 'Adventurer', supportsSkin: true,  supportsHair: true },
  { id: 'open-peeps', label: 'Open Peeps', supportsSkin: true,  supportsHair: true },
];

export function getPaAvatarStyleDef(id: PaAvatarStyle): PaAvatarStyleDef {
  return PA_AVATAR_STYLE_DEFS.find(d => d.id === id) ?? PA_AVATAR_STYLE_DEFS[0]!;
}

// Palette curate (hex a 6 cifre SENZA `#`, formato nativo DiceBear).
export const PA_AVATAR_SKIN_TONES: readonly string[] = [
  'f2d3b1', 'ecad80', 'd08b5b', 'ae5d29', '614335',
];
export const PA_AVATAR_HAIR_COLORS: readonly string[] = [
  '0e0e0e', '3b2219', '6a4e35', 'a55728', 'b58143', 'd6b370', 'c93305', 'e8e1e1',
];
export const PA_AVATAR_BACKGROUNDS: readonly string[] = [
  'b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf', 'd3f4d1', 'f1f4f5',
];

/** Default deterministico quando l'utente non ha ancora scelto (mai "icona rotta"). */
export function defaultPaAvatarConfig(seed: string): PaAvatarConfig {
  return { style: 'personas', seed, options: { backgroundColor: 'b6e3f4' } };
}

function renderResult(cfg: PaAvatarConfig): Result {
  const o = cfg.options ?? {};
  const base = {
    seed: cfg.seed,
    flip: o.flip === true,
    ...(o.backgroundColor ? { backgroundColor: [o.backgroundColor] } : {}),
  };
  switch (cfg.style) {
    case 'personas':
      return createAvatar(personas, {
        ...base,
        ...(o.skinColor ? { skinColor: [o.skinColor] } : {}),
        ...(o.hairColor ? { hairColor: [o.hairColor] } : {}),
      });
    case 'notionists':
      return createAvatar(notionists, base);
    case 'lorelei':
      return createAvatar(lorelei, {
        ...base,
        ...(o.skinColor ? { skinColor: [o.skinColor] } : {}),
        ...(o.hairColor ? { hairColor: [o.hairColor] } : {}),
      });
    case 'micah':
      return createAvatar(micah, {
        ...base,
        // micah: l'incarnato è `baseColor`.
        ...(o.skinColor ? { baseColor: [o.skinColor] } : {}),
        ...(o.hairColor ? { hairColor: [o.hairColor] } : {}),
      });
    case 'adventurer':
      return createAvatar(adventurer, {
        ...base,
        ...(o.skinColor ? { skinColor: [o.skinColor] } : {}),
        ...(o.hairColor ? { hairColor: [o.hairColor] } : {}),
      });
    case 'open-peeps':
      return createAvatar(openPeeps, {
        ...base,
        ...(o.skinColor ? { skinColor: [o.skinColor] } : {}),
        // open-peeps: capelli/tratti testa = `headContrastColor`.
        ...(o.hairColor ? { headContrastColor: [o.hairColor] } : {}),
      });
  }
}

// Cache in-module: gli avatar sono deterministici, la chat renderizza la
// stessa faccia per molte righe messaggio → si evita di rigenerare l'SVG.
const dataUriCache = new Map<string, string>();
const DATA_URI_CACHE_MAX = 200;

export function paAvatarCacheKey(cfg: PaAvatarConfig): string {
  const o = cfg.options ?? {};
  return `${cfg.style}|${cfg.seed}|${o.backgroundColor ?? ''}|${o.skinColor ?? ''}|${o.hairColor ?? ''}|${o.flip === true ? 1 : 0}`;
}

/** SVG dell'avatar come data-URI (deterministico, generato in locale). */
export function paAvatarDataUri(cfg: PaAvatarConfig): string {
  const key = paAvatarCacheKey(cfg);
  const cached = dataUriCache.get(key);
  if (cached) return cached;
  const uri = renderResult(cfg).toDataUri();
  if (dataUriCache.size >= DATA_URI_CACHE_MAX) dataUriCache.clear();
  dataUriCache.set(key, uri);
  return uri;
}
